import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { SEO } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CONTACT } from '../../constants/images';

export const LiverGI = () => {
  const services = [
    {
      title: 'Portal Hypertension — TIPS & Variceal Embolization',
      description: 'Portal hypertension is elevated pressure in the portal venous system — most commonly caused by liver cirrhosis from alcohol, hepatitis B/C, or NASH. Back-pressure causes life-threatening variceal haemorrhage and massive ascites.',
      symptoms: ['Haematemesis — vomiting blood from ruptured varices', 'Large-volume ascites with breathlessness', 'Hepatic encephalopathy — confusion', 'Recurrent ascites requiring repeated drainage'],
      treatments: ['TIPS (Transjugular Intrahepatic Portosystemic Shunt) — stent inside liver bypasses cirrhotic tissue', 'BRTO/PARTO for Gastric Varices', 'Effective bridge-to-transplant procedure', 'Via jugular vein in neck — no abdominal incision']
    },
    {
      title: 'Biliary Obstruction — PTBD & Biliary Stenting',
      description: 'The bile duct can be blocked by gallstones, inflammatory strictures, or cancer of the bile duct, pancreas, or liver — causing obstructive jaundice, cholangitis, and sepsis.',
      symptoms: ['Yellowing of skin and eyes (jaundice)', 'Dark urine and pale chalky stools', 'Fever and chills (cholangitis)', 'Abdominal pain, nausea, and itching'],
      treatments: ['PTBD (Percutaneous Transhepatic Biliary Drainage)', 'Metallic Biliary Stent placement', 'Relief from obstruction and sepsis']
    },
    {
      title: 'Liver Abscess Drainage',
      description: 'Liver abscess requires drainage to prevent sepsis and liver damage. Percutaneous image-guided drainage avoids the need for surgery.',
      symptoms: ['High fever with chills', 'Right upper abdominal pain', 'Weight loss and weakness'],
      treatments: ['Percutaneous image-guided drainage', 'No surgery required in vast majority of cases', 'Patients typically improve within 24-48 hours']
    },
    {
      title: 'Liver Cancer Treatment — TACE, TARE & Ablation',
      description: 'Minimally invasive treatments for hepatocellular carcinoma (HCC) and liver metastases. Dr. Punit performs these procedures in Mumbai as part of a multidisciplinary oncology team.',
      symptoms: ['Confirmed HCC on imaging', 'Selected liver metastases', 'Suitable liver function for treatment'],
      treatments: ['TACE — Transarterial Chemoembolization (concentrated chemotherapy delivered locally)', 'TARE — Transarterial Radioembolization (Y-90 microspheres)', 'Microwave Ablation — precision heat destroys tumour cells', 'Can be curative for small early-stage HCC']
    },
    {
      title: 'Mesenteric Ischaemia — Revascularisation',
      description: 'Reduced blood flow to the intestines — from acute clots or chronic narrowing of mesenteric arteries — causes severe abdominal pain and can lead to bowel infarction if untreated urgently.',
      symptoms: ['Severe abdominal pain', 'Pain disproportionate to physical findings', 'Postprandial pain (chronic cases)'],
      treatments: ['Catheter-directed thrombolysis for acute clots', 'Angioplasty and stenting for chronic stenosis', 'Restores intestinal perfusion']
    }
  ];

  const faqs = [
    { q: 'What is TACE and how does it treat liver cancer in Mumbai?', a: 'TACE delivers chemotherapy directly to liver tumours through a catheter in the hepatic artery — without systemic side effects. Used for HCC and liver metastases.' },
    { q: 'What is the difference between TACE and TARE for liver cancer?', a: 'TACE uses chemotherapy drugs with embolic particles. TARE uses radioactive Y-90 microspheres — targeted internal radiotherapy. The choice depends on tumour type, size, liver function, and stage.' },
    { q: 'What is a TIPS procedure and who needs it?', a: 'TIPS relieves high portal venous pressure in cirrhosis patients with recurrent variceal bleeding or refractory ascites. Performed through a vein in the neck — no abdominal surgery needed.' },
    { q: 'Can liver abscess be treated without surgery?', a: 'In the vast majority of cases, yes. Percutaneous drainage under imaging guidance drains the abscess completely, avoiding surgery. Patients typically improve within 24-48 hours.' },
    { q: 'Can microwave ablation cure liver cancer?', a: 'For small early-stage HCCs (typically under 3 cm), microwave ablation can achieve complete tumour destruction and is considered curative in selected patients.' }
  ];

  return (
    <>
      <SEO 
        title="Liver Cancer, TACE & TIPS Treatment in Mumbai | Dr. Punit Mahajan"
        description="TACE, TARE, microwave ablation, TIPS, PTBD & biliary stenting for liver cancer, portal hypertension & biliary obstruction in Mumbai by Dr. Punit Mahajan, Interventional Radiologist."
      />

      <section className="bg-gradient-to-br from-blue-950 to-slate-900 text-white py-20 relative overflow-hidden"><div className="absolute inset-0 pattern-hero opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-text-reveal">Liver & GI Interventional Radiology</h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto animate-text-reveal" style={{ animationDelay: "300ms" }}>Precise, Non-Surgical Treatment in Mumbai</p>
        </div>
      </section>

      <section className="py-16 bg-cream-textured relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose max-w-none mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Introduction</h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              The liver, bile ducts, and gastrointestinal tract are frequent sites of serious, life-altering conditions — many of which are now treatable without major surgery. Dr. Punit Mahajan, trained at Tata Memorial Hospital and KEM Hospital in Mumbai, brings rare hepato-biliary IR expertise for a wide range of liver and GI conditions.
            </p>
          </div>

          <div className="space-y-8">
            {services.map((service, index) => (
              <Card key={index} className="card-gold-hover transition-all bg-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">{service.title}</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Symptoms & Complications:</h4>
                      <ul className="space-y-2">
                        {service.symptoms.map((s, idx) => (
                          <li key={idx} className="flex items-start"><CheckCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-slate-600">{s}</span></li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Treatments:</h4>
                      <ul className="space-y-2">
                        {service.treatments.map((t, idx) => (
                          <li key={idx} className="flex items-start"><ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-slate-600">{t}</span></li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-cream-stripes relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <Card key={idx} className="border-l-4 border-amber-500 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-blue-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold">Expert Liver & GI Interventional Care</h2>
          <p className="text-xl text-slate-300">Trained at Tata Memorial Hospital for oncology interventions</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${CONTACT.phone}`}><Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">Call {CONTACT.phone}</Button></a>
            <Link to="/contact"><Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">Book Consultation</Button></Link>
          </div>
        </div>
      </section>
    </>
  );
};
